import { createParser, parseAsInteger, useQueryStates } from 'nuqs';
import { useEffect, useRef, useState } from 'react';

/**
 * Hook to debounce a value
 * @param value The value to debounce
 * @param delay The delay in milliseconds
 * @returns The debounced value
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Base field configuration
interface FieldConfig<T = unknown> {
  type: 'string' | 'integer';
  defaultValue?: T;
  debounceDelay?: number;
  shouldDebounce?: boolean;
}

// Fluent field builder
class FieldBuilder<T = unknown> implements FieldConfig<T> {
  type: 'string' | 'integer';
  defaultValue?: T;
  debounceDelay?: number;
  shouldDebounce?: boolean;

  constructor(type: 'string' | 'integer') {
    this.type = type;
  }

  withDefault(value: T): FieldBuilder<T> {
    this.defaultValue = value;
    return this;
  }

  withDebounce(delay: number = 500): FieldBuilder<T> {
    this.debounceDelay = delay;
    this.shouldDebounce = delay > 0;
    return this;
  }

  // Internal method to get the final configuration
  _getConfig(): FieldConfig<T> {
    return {
      type: this.type,
      defaultValue: this.defaultValue,
      debounceDelay: this.debounceDelay || 500,
      shouldDebounce: this.shouldDebounce !== undefined,
    };
  }
}

// Create field factory
export const f = {
  string: (): FieldBuilder<string> => new FieldBuilder('string'),
  integer: (): FieldBuilder<number> => new FieldBuilder('integer'),
};

const parseAsEncodedString = createParser({
  parse: (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  },
  serialize: (value: string) => encodeURIComponent(value),
});

// Helper type to extract the default value from a FieldBuilder
type ExtractFieldDefault<T> =
  T extends FieldBuilder<infer U> ? NonNullable<U> : never;

// Helper type to build the filters object type from the config
type BuildFiltersType<T extends Record<string, FieldBuilder>> = {
  [K in keyof T]: ExtractFieldDefault<T[K]>;
};

// Type for filter field registration
export interface FilterFieldProps {
  value: unknown;
  onChange: (_value: unknown) => void;
  name: string;
  type: 'string' | 'integer';
}

/**
 * Hook to manage query states with debounced filters
 * @param config Configuration object with filter definitions
 * @returns Object with filters, debouncedFilters, setFilters function, and registerFilter function
 */
export function useFilters<T extends Record<string, FieldBuilder>>(config: T) {
  // Separate debounced and non-debounced fields
  const debouncedKeys: string[] = [];
  const nonDebouncedKeys: string[] = [];
  const debounceDelays: Record<string, number> = {};

  // Build nuqs config for all fields
  const nuqsConfig: Record<string, unknown> = {};

  Object.entries(config).forEach(([key, value]) => {
    const fieldConfig = value._getConfig();

    if (fieldConfig.shouldDebounce) {
      debouncedKeys.push(key);
      debounceDelays[key] = fieldConfig.debounceDelay || 500;
    } else {
      nonDebouncedKeys.push(key);
    }

    // Create nuqs parser based on type
    nuqsConfig[key] =
      fieldConfig.type === 'string'
        ? parseAsEncodedString.withDefault(
            (fieldConfig.defaultValue as string) || ''
          )
        : parseAsInteger.withDefault((fieldConfig.defaultValue as number) || 0);
  });

  // Get query states
  const [queryFilters, setQueryFilters] = useQueryStates(nuqsConfig as any);
  const previousQueryFiltersRef = useRef<Record<string, unknown> | null>(null);

  // Create debounced versions of filters
  const [debouncedFilters, setDebouncedFilters] = useState<BuildFiltersType<T>>(
    () => {
      const initial = {} as BuildFiltersType<T>;
      (Object.keys(config) as Array<keyof T>).forEach(key => {
        initial[key] = queryFilters[
          key as string
        ] as BuildFiltersType<T>[keyof T];
      });
      return initial;
    }
  );

  // Update debounced filters with debounce
  useEffect(() => {
    const timers: Record<string, ReturnType<typeof setTimeout>> = {};

    debouncedKeys.forEach(key => {
      if (queryFilters[key] !== debouncedFilters[key]) {
        timers[key] = setTimeout(() => {
          setDebouncedFilters((prev: BuildFiltersType<T>) => ({
            ...prev,
            [key]: queryFilters[key],
          }));
        }, debounceDelays[key]);
      }
    });

    // Update non-debounced filters immediately
    nonDebouncedKeys.forEach(key => {
      if (queryFilters[key] !== debouncedFilters[key]) {
        setDebouncedFilters((prev: BuildFiltersType<T>) => ({
          ...prev,
          [key]: queryFilters[key],
        }));
      }
    });

    return () => {
      Object.values(timers).forEach(timer => clearTimeout(timer));
    };
  }, [
    queryFilters,
    debouncedFilters,
    debouncedKeys,
    nonDebouncedKeys,
    debounceDelays,
  ]);

  useEffect(() => {
    const previousQueryFilters = previousQueryFiltersRef.current;
    previousQueryFiltersRef.current = queryFilters as Record<string, unknown>;

    if (!previousQueryFilters || !('pageNo' in queryFilters)) {
      return;
    }

    const hasFilterChange = Object.keys(queryFilters).some(key => {
      if (key === 'pageNo' || key === 'pageSize') {
        return false;
      }

      return previousQueryFilters[key] !== queryFilters[key];
    });

    if (!hasFilterChange || queryFilters.pageNo === 1) {
      return;
    }

    setQueryFilters(prev => ({
      ...prev,
      pageNo: 1,
    }));
  }, [queryFilters, setQueryFilters]);

  // Function to register a filter field
  const registerFilter = (name: keyof T) => {
    const fieldConfig = config[name]._getConfig();

    return {
      name: name as string,
      value: queryFilters[name as string],
      onChange: (newValue: unknown) => {
        setQueryFilters(prev => ({
          ...prev,
          [name]: newValue,
        }));
      },
      type: fieldConfig.type,
    };
  };

  return {
    filters: queryFilters as BuildFiltersType<T>,
    debouncedFilters: debouncedFilters as BuildFiltersType<T>,
    setFilters: setQueryFilters as (
      _filters: Partial<BuildFiltersType<T>>
    ) => void,
    registerFilter,
  };
}

// Export the old hook for backward compatibility
export const useDebouncedFilters = useFilters;
